import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Download,
  Receipt,
  Eye,
  X
} from 'lucide-react';
import { formatDate } from '../../utils/stringUtils';
import essService from '../../api/services/essService';
import { toast } from 'react-hot-toast';

import PayslipDetailView from '../../components/payroll/PayslipDetailView';
import { payslipsApi } from '../../api/payslipsApi';

const formatPeriodString = (periodString) => {
  if (!periodString) return 'N/A';

  // Parse period string like "01/11/2025 - 30/11/2025"
  const parts = periodString.split(' - ');
  if (parts.length !== 2) return periodString; // Return original if format is unexpected

  try {
    // Parse DD/MM/YYYY format
    const parseDate = (dateStr) => {
      const [day, month, year] = dateStr.trim().split('/');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    };

    const startDate = parseDate(parts[0]);
    const endDate = parseDate(parts[1]);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return periodString; // Return original if parsing fails
    }

    const startFormatted = formatDate(startDate, { includeTime: false });
    const endFormatted = formatDate(endDate, { includeTime: false });

    return `${startFormatted} - ${endFormatted}`;
  } catch (error) {
    return periodString; // Return original if any error occurs
  }
};

const MyPayslips = () => {
  // selectedMonth: 'YYYY-MM' string, or '' for all
  const [selectedMonth, setSelectedMonth] = useState(
    () => {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    }
  );
  const [downloadingId, setDownloadingId] = useState(null);
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);

  // Fetch ALL payslips from API (no year filter - we filter locally)
  const { data: payslipsData, isLoading: isLoadingPayslips, refetch: refetchPayslips } = useQuery({
    queryKey: ['employeePayslips'],
    queryFn: async () => {
      const result = await essService.getPayslips(); // No year param - fetch all
      if (result.success) {
        return result.data || [];
      } else {
        toast.error(result.error || 'Failed to fetch payslips');
        return [];
      }
    },
    staleTime: 0,
    onError: (error) => {
      toast.error('Failed to load payslips');
    }
  });

  // Map API response to component format, filtered by selectedMonth (YYYY-MM)
  const payslips = useMemo(() => {
    if (!payslipsData || !Array.isArray(payslipsData)) return [];

    return payslipsData
      .filter(payslip => {
        if (!selectedMonth) return true; // no filter → show all
        const endDate = payslip.payPeriod?.endDate || payslip.generatedDate || payslip.createdAt;
        if (!endDate) return true;
        const d = new Date(endDate);
        const payslipYM = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        return payslipYM === selectedMonth;
      })
      .map(payslip => ({
        id: payslip.id || payslip.payslipId,
        payslipId: payslip.payslipId,
        month: payslip.month,
        year: payslip.year,
        period: payslip.period,
        generatedDate: payslip.generatedDate,
        status: payslip.status === 'generated' || payslip.status === 'sent' ? 'generated' : payslip.status || 'generated',
        netPay: payslip.netPay || payslip.netSalary || 0,
        grossPay: payslip.grossPay || 0,
        totalDeductions: payslip.totalDeductions || 0,
        basicSalary: payslip.basicSalary || 0,
        allowances: payslip.allowances || {},
        deductions: payslip.deductions || {},
        employeeName: payslip.employeeName || 'N/A',
        employeeId: payslip.employeeId || 'N/A',
        department: payslip.department || 'N/A',
        workingDays: payslip.workingDays || 0,
        presentDays: payslip.presentDays || 0,
        components: payslip.components || { earnings: [], deductions: [] },
        salaryStructure: payslip.salaryStructure || null,
        downloadUrl: payslip.downloadUrl
      }));
  }, [payslipsData, selectedMonth]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'generated':
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'withheld':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };


  const handleDownloadPayslip = async (payslip) => {
    if (!payslip.id) return;

    try {
      setDownloadingId(payslip.id);

      // Find the full payslip data
      const fullPayslip = payslips.find(p => p.id === payslip.id);

      if (!fullPayslip) {
        toast.error('Payslip not found');
        setDownloadingId(null);
        return;
      }

      const downloadResponse = await payslipsApi.downloadPayslip(payslip.id);
      
      const blob = downloadResponse.data instanceof Blob ? downloadResponse.data : (downloadResponse instanceof Blob ? downloadResponse : new Blob([downloadResponse]));
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Fallback filename
      let fileName = `Payslip_${fullPayslip.employeeName?.replace(/\s+/g, '_') || 'Employee'}_${fullPayslip.employeeId || fullPayslip.id}_${fullPayslip.month}_${fullPayslip.year}.pdf`;
      
      // Try to get filename from content-disposition header if available
      if (downloadResponse.headers && downloadResponse.headers['content-disposition']) {
        const match = downloadResponse.headers['content-disposition'].match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          fileName = match[1];
        }
      }
      
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Payslip downloaded successfully!');
      setDownloadingId(null);
    } catch (error) {
      toast.error(`Failed to generate payslip PDF: ${error.message || 'Unknown error occurred. Please try again later.'}`);
      setDownloadingId(null);
    }
  };

  const handleViewPayslip = async (payslip) => {
    try {
      const response = await payslipsApi.getPayslipById(payslip.id);
      const full =
        response?.responseCode === 200 && response?.data
          ? response.data
          : response?.data || response;
      setSelectedPayslip(full || payslip);
      setShowPayslipModal(true);
    } catch {
      setSelectedPayslip(payslip);
      setShowPayslipModal(true);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const PayslipModal = () => {
    if (!showPayslipModal || !selectedPayslip) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
        <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-end z-10">
            <button
              onClick={() => setShowPayslipModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <PayslipDetailView payslip={selectedPayslip} />
        </div>
      </div>
    );
  };



  if (isLoadingPayslips) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading payslips...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Payslips</h1>
          <div className="flex items-end gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Month</label>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
              />
            </div>
            {selectedMonth && (
              <button
                onClick={() => setSelectedMonth('')}
                className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Payslips Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Month
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Generated At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {payslips.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    <Receipt className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-sm font-medium text-gray-900">No payslips found</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      No payslips available for the selected year.
                    </p>
                  </td>
                </tr>
              ) : (
                payslips.map((payslip) => (
                  <tr key={payslip.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {payslip.month} {payslip.year}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {payslip.period
                          ? (payslip.period.includes(' - ')
                            ? payslip.period.split(' - ').map(dateStr => {
                              try {
                                // Try to parse DD/MM/YYYY format
                                const [day, month, year] = dateStr.trim().split('/');
                                if (day && month && year) {
                                  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                                  return formatDate(date.toISOString(), { includeTime: false });
                                }
                                return formatDate(dateStr, { includeTime: false });
                              } catch {
                                return formatDate(dateStr, { includeTime: false });
                              }
                            }).join(' - ')
                            : formatDate(payslip.period, { includeTime: false }))
                          : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(payslip.generatedDate)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payslip.status)}`}>
                        {payslip.status ? payslip.status.charAt(0).toUpperCase() + payslip.status.slice(1) : 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewPayslip(payslip)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Payslip"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDownloadPayslip(payslip)}
                          disabled={downloadingId === payslip.id}
                          className="text-green-600 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Download Payslip"
                        >
                          {downloadingId === payslip.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>



      {/* Payslip Modal */}
      {showPayslipModal && <PayslipModal />}
    </div>
  );
};

export default MyPayslips;
